import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import Fuse, { IFuseOptions } from 'fuse.js';

import { DropdownOption } from 'components/Dropdown';

import { courseStatusOptions } from 'constants/courseStatusOptions';

import { StudentOptionFragment } from 'graphql/types/generated';

import {
  CoursesFiltersState,
  CoursesPageType,
  CoursesPayload,
  CoursesState,
  CoursesView,
  FilterableCourse,
  RootState,
} from './types';

/**
 * Reducers
 */
const initialState: CoursesState = {
  editable: {
    view: 'grid',
    courses: [],
    filters: {
      currentStatus: courseStatusOptions[0],
      currentEnrolledStudents: [],
      currentGradeYears: [],
      currentSubjects: [],
      currentLabels: [],
      currentOrganizationCourseGroups: [],
    },
    searchQuery: '',
    hasSetDefaultFilters: false,
    selectionMap: {},
  },
  readonly: {
    view: 'grid',
    courses: [],
    filters: {
      currentSubjects: [],
      currentLabels: [],
    },
    searchQuery: '',
    hasSetDefaultFilters: false,
    selectionMap: {},
  },
  organization_portal: {
    view: 'list',
    courses: [],
    filters: {
      currentGradeYears: [],
      currentSubjects: [],
      currentLabels: [],
    },
    searchQuery: '',
    hasSetDefaultFilters: false,
    selectionMap: {},
  },
};

export const coursesSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    setCourses: (
      state: CoursesState,
      action: PayloadAction<CoursesPayload<FilterableCourse[]>>
    ) => {
      const type = action.payload.type;
      state[type].courses = action.payload.value;
    },
    resetFilters: (
      state: CoursesState,
      action: PayloadAction<CoursesPageType>
    ) => {
      state[action.payload].filters = initialState[action.payload].filters;
    },
    setCoursesView: (
      state: CoursesState,
      action: PayloadAction<CoursesPayload<CoursesView>>
    ) => {
      state[action.payload.type].view = action.payload.value;
    },
    setCoursesSearchQuery: (
      state: CoursesState,
      action: PayloadAction<CoursesPayload<string>>
    ) => {
      const type = action.payload.type;
      state[type].searchQuery = action.payload.value;
    },
    setCurrentStatus: (
      state: CoursesState,
      action: PayloadAction<CoursesPayload<DropdownOption<string>>>
    ) => {
      const type = action.payload.type;
      state[type].filters.currentStatus = action.payload.value;
    },
    setCurrentEnrolledStudents: (
      state: CoursesState,
      action: PayloadAction<
        CoursesPayload<DropdownOption<StudentOptionFragment>[]>
      >
    ) => {
      const type = action.payload.type;
      state[type].filters.currentEnrolledStudents = action.payload.value;
    },
    setCurrentSubjects: (
      state: CoursesState,
      action: PayloadAction<CoursesPayload<DropdownOption<string>[]>>
    ) => {
      const type = action.payload.type;
      state[type].filters.currentSubjects = action.payload.value;
    },
    setCurrentLabels: (
      state: CoursesState,
      action: PayloadAction<CoursesPayload<DropdownOption<string>[]>>
    ) => {
      const type = action.payload.type;
      state[type].filters.currentLabels = action.payload.value;
    },
    setCurrentGradeYears: (
      state: CoursesState,
      action: PayloadAction<CoursesPayload<DropdownOption<string>[]>>
    ) => {
      const type = action.payload.type;
      state[type].filters.currentGradeYears = action.payload.value;
    },
    setCurrentOrganizationCourseGroups: (
      state: CoursesState,
      action: PayloadAction<CoursesPayload<DropdownOption<string>[]>>
    ) => {
      const type = action.payload.type;
      state[type].filters.currentOrganizationCourseGroups =
        action.payload.value;
    },
    setHasSetDefaultFilters: (
      state: CoursesState,
      action: PayloadAction<CoursesPayload<boolean>>
    ) => {
      const type = action.payload.type;
      state[type].hasSetDefaultFilters = action.payload.value;
    },
    toggleCourseSelection: (
      state: CoursesState,
      action: PayloadAction<CoursesPayload<string>>
    ) => {
      const type = action.payload.type;
      const courseId = action.payload.value;
      if (state[type].selectionMap[courseId]) {
        delete state[type].selectionMap[courseId];
      } else {
        state[type].selectionMap[courseId] = true;
      }
    },
    selectAllCourses: (
      state: CoursesState,
      action: PayloadAction<CoursesPayload<string[]>>
    ) => {
      const type = action.payload.type;
      const courseIds = action.payload.value;
      state[type].selectionMap = Object.fromEntries(
        courseIds.map((id) => [id, true])
      );
    },
    clearCourseSelection: (
      state: CoursesState,
      action: PayloadAction<CoursesPageType>
    ) => {
      state[action.payload].selectionMap = {};
    },
  },
});

/**
 * Actions
 */
export const {
  setCourses,
  resetFilters,
  setCoursesView,
  setCoursesSearchQuery,
  setCurrentStatus,
  setCurrentEnrolledStudents,
  setCurrentSubjects,
  setCurrentLabels,
  setCurrentGradeYears,
  setCurrentOrganizationCourseGroups,
  setHasSetDefaultFilters,
  toggleCourseSelection,
  selectAllCourses,
  clearCourseSelection,
} = coursesSlice.actions;

/**
 * Selectors
 */
export const getCourses = (state: RootState): CoursesState => state.courses;

export const getAllCourses = (type: CoursesPageType) =>
  createSelector(getCourses, (courses) => courses[type].courses);

export const getCoursesView = (type: CoursesPageType) =>
  createSelector(getCourses, (courses: CoursesState) => courses[type].view);

export const getCoursesSearchQuery = (type: CoursesPageType) =>
  createSelector(getCourses, (courses) => courses[type].searchQuery);

export const getCoursesFilters = (type: CoursesPageType) =>
  createSelector(getCourses, (courses: CoursesState) => courses[type].filters);

export const getHasSetDefaultFilters = (type: CoursesPageType) =>
  createSelector(
    getCourses,
    (courses: CoursesState) => courses[type].hasSetDefaultFilters ?? false
  );

export const getNumFiltersApplied = (type: CoursesPageType) =>
  createSelector(getCoursesFilters(type), (filters: CoursesFiltersState) => {
    const {
      currentStatus,
      currentEnrolledStudents,
      currentSubjects,
      currentLabels,
      currentGradeYears,
      currentOrganizationCourseGroups,
    } = filters;

    return (
      (currentStatus ? 1 : 0) +
      (currentEnrolledStudents?.length ?? 0) +
      (currentSubjects?.length ?? 0) +
      (currentLabels?.length ?? 0) +
      (currentGradeYears?.length ?? 0) +
      (currentOrganizationCourseGroups?.length ?? 0)
    );
  });

const fuseOptions: IFuseOptions<FilterableCourse> = {
  keys: [
    { name: 'name', weight: 1 },
    { name: 'subjects', weight: 1 },
    { name: 'labels', weight: 1 },
    { name: 'gradeYears', weight: 1 },
    { name: 'gradeYearsLabels', weight: 1 },
    { name: 'studentNames', weight: 1 },
    { name: 'description', weight: 1 },
    {
      name: 'organizationCourseGroups',
      weight: 1,
      getFn: (course) =>
        course.organizationCourseGroups?.map(
          (group) => group.organizationCourseGroup.name
        ) ?? [],
    },
  ],
  threshold: 0.2,
  ignoreLocation: true,
  ignoreFieldNorm: true,
};

export const filterCourses = (
  searchQuery: string,
  courses: FilterableCourse[],
  currentStatus: DropdownOption<string> | null = null,
  currentEnrolledStudents: DropdownOption<StudentOptionFragment>[] = [],
  currentSubjects: DropdownOption<string>[] = [],
  currentLabels: DropdownOption<string>[] = [],
  currentGradeYears: DropdownOption<string>[] = [],
  currentOrganizationCourseGroups: DropdownOption<string>[] = [],
  type?: CoursesPageType
) => {
  // Search Results via Search Query
  const fuse = new Fuse(courses, fuseOptions);
  const searchResults = searchQuery
    ? fuse.search(searchQuery).map((result) => result.item)
    : courses;

  // Filtered Search Results via Filters
  return searchResults.filter((course) => {
    // Status Filter
    const courseStatus = course.archived ? 'Archived' : 'Active';
    const matchesCourseStatus =
      !currentStatus || currentStatus.value === courseStatus;

    // Enrolled Students Filter
    const currentEnrolledStudentsIds = currentEnrolledStudents.map(
      (student) => student.value.id
    );
    const courseEnrolledStudentIds =
      course.studentCourses?.flatMap(
        (studentCourse) => studentCourse.student.id
      ) ?? [];
    const matchesEnrolledStudents =
      currentEnrolledStudentsIds.length === 0 ||
      currentEnrolledStudentsIds.some((studentId) =>
        courseEnrolledStudentIds.includes(studentId)
      );

    // Subjects Filter
    const currentSubjectsValues = currentSubjects.map(
      (subject) => subject.value
    );
    const subjects = (course.subjects ?? []) as string[];
    const matchesSubjects =
      currentSubjectsValues.length === 0 ||
      currentSubjectsValues.some((subject) => subjects.includes(subject));

    // Labels Filter
    const currentLabelsValues = currentLabels.map((label) => label.value);
    const labels = (course.labels ?? []) as string[];
    const courseGroupNames =
      type === 'organization_portal'
        ? course.organizationCourseGroups?.map(
            (groupRelation) => groupRelation.organizationCourseGroup.name
          ) ?? []
        : [];

    const allLabelOptions = [...labels, ...courseGroupNames];
    const matchesLabels =
      currentLabelsValues.length === 0 ||
      currentLabelsValues.some((label) => allLabelOptions.includes(label));

    // Grade Years Filter
    const currentGradeYearValues = currentGradeYears.map(
      (gradeYear) => gradeYear.value
    );
    const gradeYears = (course.gradeYears ?? []) as string[];
    const matchesGradeYears =
      currentGradeYearValues.length === 0 ||
      currentGradeYearValues.some((gradeYear) =>
        gradeYears.includes(gradeYear)
      );

    // Organization Course Groups Filter
    const matchesOrganizationCourseGroups =
      type === 'organization_portal'
        ? true
        : (() => {
            const currentOrganizationCourseGroupValues =
              currentOrganizationCourseGroups.map((group) => group.value);

            const organizationCourseGroupIds =
              course.organizationCourseGroups.map(
                (groupRelation) => groupRelation.organizationCourseGroup.id
              );

            return (
              currentOrganizationCourseGroupValues.length === 0 ||
              currentOrganizationCourseGroupValues.some((groupId) =>
                organizationCourseGroupIds.includes(groupId)
              )
            );
          })();

    return (
      matchesCourseStatus &&
      matchesEnrolledStudents &&
      matchesGradeYears &&
      matchesSubjects &&
      matchesLabels &&
      matchesOrganizationCourseGroups
    );
  });
};

export const getCourseSelectionMap = (type: CoursesPageType) =>
  createSelector(getCourses, (courses) => courses[type].selectionMap);

export const getSelectedCourseIds = (type: CoursesPageType) =>
  createSelector(getCourseSelectionMap(type), (selectionMap) =>
    Object.keys(selectionMap)
  );

export const getSelectedCourseCount = (type: CoursesPageType) =>
  createSelector(
    getSelectedCourseIds(type),
    (selectedIds) => selectedIds.length
  );

export const getFilteredCourses = (type: CoursesPageType) =>
  createSelector(
    getAllCourses(type),
    getCoursesFilters(type),
    getCoursesSearchQuery(type),
    (allFilterableCourses, filters, searchQuery) => {
      const {
        currentStatus,
        currentEnrolledStudents,
        currentSubjects,
        currentLabels,
        currentGradeYears,
        currentOrganizationCourseGroups,
      } = filters;

      return filterCourses(
        searchQuery,
        allFilterableCourses,
        currentStatus,
        currentEnrolledStudents,
        currentSubjects,
        currentLabels,
        currentGradeYears,
        currentOrganizationCourseGroups,
        type
      );
    }
  );
